import { onBeforeUnmount, ref, type Ref } from 'vue'

// Persistable, drag-resizable sidebar width. Owned by WikiSidebar so
// the existing `w-72` Tailwind utility (288px) becomes a starting
// default that the user can drag to a preferred width and have stick
// across reloads.
//
// The composable returns a width ref + a `startResize` event handler
// the host attaches to a drag handle. Drag updates the ref live;
// release commits to localStorage. Touch isn't wired today — desktop
// only — because the mobile sidebar fills the viewport (the host
// doesn't render the handle below the `sm:` breakpoint).

const STORAGE_KEY = 'superbrain:sidebar-width'

// Clamp range. Lower bound is narrower than today's 288px so users on
// small screens can shave horizontal real estate; upper bound stops
// the sidebar from eating half a typical 1280-1440px laptop screen.
const MIN_WIDTH = 220
const MAX_WIDTH = 480
const DEFAULT_WIDTH = 288

export interface SidebarWidth {
  width: Ref<number>
  startResize: (event: PointerEvent) => void
}

export function useSidebarWidth(): SidebarWidth {
  const width = ref<number>(loadPersisted())

  // Drag state lives outside the reactive surface — readers don't need
  // to know we're mid-drag, only the eventual width matters. Captured
  // at pointerdown so pointermove can compute the new width relative
  // to the start position rather than absolute viewport coords (which
  // would jump if the cursor wasn't already on the handle when drag
  // began).
  let dragStartX = 0
  let dragStartWidth = 0
  let active = false

  function startResize(event: PointerEvent): void {
    event.preventDefault()
    dragStartX = event.clientX
    dragStartWidth = width.value
    active = true
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp, { once: true })
  }

  function onPointerMove(event: PointerEvent): void {
    if (!active) return
    const delta = event.clientX - dragStartX
    width.value = clamp(dragStartWidth + delta)
  }

  function onPointerUp(): void {
    active = false
    window.removeEventListener('pointermove', onPointerMove)
    persist(width.value)
  }

  // Defensive: if the host unmounts mid-drag (route change, drawer
  // close), tear down the global listeners so they don't leak.
  onBeforeUnmount(() => {
    if (!active) return
    active = false
    window.removeEventListener('pointermove', onPointerMove)
  })

  return { width, startResize }
}

function clamp(value: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)))
}

function loadPersisted(): number {
  if (typeof window === 'undefined') return DEFAULT_WIDTH
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return DEFAULT_WIDTH
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed)) return DEFAULT_WIDTH
  return clamp(parsed)
}

function persist(value: number): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // localStorage can throw under quota / privacy mode — drop the
    // persistence silently rather than failing a UI interaction. The
    // width still applies for the rest of the session.
  }
}
